import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"
import { cn } from "@/lib/utils"

const toolTileVariants = cva(
  "relative group flex flex-col rounded-lg border bg-card p-6 transition-all hover:shadow-lg hover:border-primary cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
  {
    variants: {
      variant: {
        default: "hover:bg-accent/50",
        active: "border-primary bg-primary/5 shadow-md",
      },
      size: {
        sm: "p-4",
        default: "p-6",
        lg: "p-8",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface ToolTileProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof toolTileVariants> {
  icon?: React.ReactNode
  title: string
  description?: string
  badge?: React.ReactNode
  disabled?: boolean
}

const ToolTile = React.forwardRef<HTMLDivElement, ToolTileProps>(
  (
    {
      className,
      variant,
      size,
      icon,
      title,
      description,
      badge,
      disabled,
      ...props
    },
    ref
  ) => (
    <div
      ref={ref}
      className={cn(toolTileVariants({ variant, size, className }), {
        "opacity-50 cursor-not-allowed": disabled,
      })}
      role="button"
      tabIndex={disabled ? -1 : 0}
      {...props}
    >
      {badge && (
        <div className="absolute top-3 right-3">
          {badge}
        </div>
      )}
      {icon && (
        <div className="mb-3 text-2xl text-primary">{icon}</div>
      )}
      <h3 className="font-semibold text-foreground mb-1">{title}</h3>
      {description && (
        <p className="text-sm text-muted-foreground">{description}</p>
      )}
    </div>
  )
)
ToolTile.displayName = "ToolTile"

export { ToolTile, toolTileVariants }
